import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateOpportunityCommentRequest, UpdateOpportunityCommentRequest } from 'backend/types'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  createOpportunityComment,
  deleteOpportunityComment,
  getOpportunityComments,
  updateOpportunityComment,
} from '@/lib'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/stores/auth'
import type { CommentWithNumber } from '../types'

interface UseCommentsSectionOptions {
  opportunityId: number
  onCommentsLoaded?: () => void
}

/**
 * コメントセクション用カスタムフック
 *
 * コメントのCRUD操作、リロード制御、自動スクロールを管理
 */
export function useCommentsSection({ opportunityId, onCommentsLoaded }: UseCommentsSectionOptions) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasCalledOnLoad = useRef(false)
  const user = useAuthStore((state) => state.user)
  const queryClient = useQueryClient()

  const currentMemberId = user?.memberId ?? null
  const isAdministrator = user?.acl?.administrator ?? false

  // コメント一覧取得
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.opportunity.comments(opportunityId),
    queryFn: async () => {
      return getOpportunityComments(opportunityId)
    },
    enabled: opportunityId > 0,
  })

  // コメント作成
  const createMutation = useMutation({
    mutationFn: async (commentData: CreateOpportunityCommentRequest) => {
      return createOpportunityComment(opportunityId, commentData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.comments(opportunityId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.all(opportunityId) })
      toast.success('コメントを投稿しました')
    },
    onError: (error: Error) => {
      toast.error(`コメント投稿に失敗しました: ${error.message}`)
    },
  })

  // コメント更新
  const updateMutation = useMutation({
    mutationFn: async ({
      commentId,
      data: updateData,
    }: {
      commentId: number
      data: UpdateOpportunityCommentRequest
    }) => {
      return updateOpportunityComment(opportunityId, commentId, updateData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.comments(opportunityId) })
      toast.success('コメントを更新しました')
    },
    onError: (error: Error) => {
      toast.error(`コメント更新に失敗しました: ${error.message}`)
    },
  })

  // コメント削除
  const deleteMutation = useMutation({
    mutationFn: async (commentId: number) => {
      return deleteOpportunityComment(opportunityId, commentId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.comments(opportunityId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.all(opportunityId) })
      toast.success('コメントを削除しました')
    },
    onError: (error: Error) => {
      toast.error(`コメント削除に失敗しました: ${error.message}`)
    },
  })

  // スクロール制御
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [])

  // 初回読み込み時に最下部へスクロール & onCommentsLoaded 呼び出し
  const commentsData = data?.comments
  useEffect(() => {
    if (commentsData && commentsData.length > 0) {
      scrollToBottom()
    }
  }, [commentsData, scrollToBottom])

  useEffect(() => {
    if (data && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true
      onCommentsLoaded?.()
    }
  }, [data, onCommentsLoaded])

  // リロード処理
  const handleReload = useCallback(async () => {
    await refetch()
    toast.success('コメントを再読み込みしました')
  }, [refetch])

  // コメント作成
  const handleCreate = useCallback(
    async (comment: string) => {
      await createMutation.mutateAsync({ comment })
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    },
    [createMutation, scrollToBottom],
  )

  // コメント更新
  const handleUpdate = useCallback(
    async (commentId: number, comment: string) => {
      await updateMutation.mutateAsync({ commentId, data: { comment } })
    },
    [updateMutation],
  )

  // コメント削除
  const handleDelete = useCallback(
    async (commentId: number) => {
      await deleteMutation.mutateAsync(commentId)
    },
    [deleteMutation],
  )

  // コメント番号を付与
  const commentsWithNumber: CommentWithNumber[] = (data?.comments ?? []).map((comment, index) => ({
    ...comment,
    commentNumber: index + 1,
  }))

  return {
    // データ
    comments: commentsWithNumber,
    isLoading,
    isRefetching: isFetching && !isLoading,
    error,
    currentMemberId,
    isAdministrator,

    // 状態
    scrollContainerRef,

    // ミューテーション状態
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // ハンドラー
    handleReload,
    handleCreate,
    handleUpdate,
    handleDelete,
  }
}
